import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Menu } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ActionMenuItem from "@entrilia/shared/components/ActionMenuItem";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import FolderManagedIcon from "@entrilia/shared/icons/FolderManagedIcon";
import biApi from "../../../../../../api/biApi";
import type { ClientInfoExtended, ReportConfiguration, ReportTemplate } from "../../../../../../api/types/svTypes.ts";
import { getOrganizationReportTemplatesUrl } from "../../../report-lists/reports/utilities/editReportUrl.ts";
import DeleteTemplateDialog from "../../../report-lists/templates/DeleteTemplateDialog.tsx";
import SaveTemplateDialog from "../../../report-lists/templates/SaveTemplateDialog.tsx";
import SourceEnvironmentIcon from "@entrilia/shared/icons/SourceEnvironmentIcon";
import DataSourceDialog from "./dialogs/DataSourceDialog";

interface Props {
  anchorEl: HTMLElement | null;
  template: ReportTemplate;
  companies: ClientInfoExtended[];
  onClose: () => void;
  onEdited: (rt: ReportTemplate, dataSourceChanged?: boolean) => void;
  onDataSourceChanged: (ds: string) => void;
  getTemplateConfigurationAsync: () => Promise<ReportConfiguration | undefined>;
}

export default function TemplateActions({
  anchorEl,
  template,
  companies,
  onClose,
  onEdited,
  getTemplateConfigurationAsync,
  onDataSourceChanged,
}: Props) {
  const { sendNotification } = useNotificationContext();
  const [openEditDialog, setOpenEditDialog] = useState<boolean>();
  const [openChangeDataSourceDialog, setOpenChangeDataSourceDialog] = useState<boolean>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>();
  const [open, setOpen] = useState(() => !!anchorEl);

  const onDeleteReport = useCallback(() => {
    setOpen(false);
    setOpenDeleteDialog(true);
  }, [setOpenDeleteDialog]);

  const onChangeDataSource = useCallback(() => {
    setOpen(false);
    setOpenChangeDataSourceDialog(true);
  }, [setOpenChangeDataSourceDialog]);

  const onEditTemplate = useCallback(() => {
    setOpen(false);
    setOpenEditDialog(true);
  }, [setOpenEditDialog]);

  useEffect(() => {
    if (openDeleteDialog === false || openChangeDataSourceDialog === false || openEditDialog === false) {
      onClose();
    }
  }, [openDeleteDialog, openChangeDataSourceDialog, openEditDialog, onClose]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => onClose()}
        slotProps={{
          list: { sx: { width: "220px" } },
        }}
      >
        <ActionMenuItem
          key={"editTemplate"}
          icon={<FolderManagedIcon />}
          title="Edit Template information"
          onClick={onEditTemplate}
        />
        <ActionMenuItem
          key={"datasource"}
          icon={<SourceEnvironmentIcon />}
          title="Change Data source"
          onClick={onChangeDataSource}
        />
        <ActionMenuItem
          key={"delete"}
          icon={<DeleteOutlineOutlinedIcon color="error" />}
          title="Delete"
          onClick={onDeleteReport}
        />
      </Menu>
      {openChangeDataSourceDialog && template && (
        <DataSourceDialog
          template={template}
          companies={companies}
          onClose={() => setOpenChangeDataSourceDialog(false)}
          onSaved={(ds) => {
            onDataSourceChanged(ds);
            setOpenChangeDataSourceDialog(false);
          }}
        />
      )}
      {openDeleteDialog && template && (
        <DeleteTemplateDialog
          templates={[template]}
          doDelete={(templates) =>
            biApi.deleteReportTemplates({
              templates: templates.map((t) => ({ templateId: t.reportId, organization: t.organization })),
            })
          }
          onDeleted={() => {
            sendNotification("The template was successfully deleted.");
            window.open(getOrganizationReportTemplatesUrl(template.organization), "_self", "noopener noreferrer");
          }}
          onClose={() => setOpenDeleteDialog(false)}
        />
      )}
      {openEditDialog && template && (
        <SaveTemplateDialog
          title="Edit Template information"
          template={template}
          getTemplateConfigurationAsync={getTemplateConfigurationAsync}
          onClose={() => setOpenEditDialog(false)}
          onSaved={(rt, dataSourceChanged) => {
            onEdited(rt, dataSourceChanged);
            setOpenEditDialog(false);
            sendNotification("The template was successfully saved.");
          }}
          companies={companies}
        />
      )}
    </>
  );
}
